import {
  ChevronDownRegularIcon,
  HouseMedicalChimneyRegularIcon,
} from "@elixir/icons";
import { CUSTOM_SLOT_TYPE, SlotTime } from "@elixir/types";
import dateFormat from "dateformat";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
  InputField,
  PopoverContainerUi,
  PrimaryButton,
  SelectAvailableSlotComponentUi,
  SelectDateComponentUi,
  SelectDurationComponentUi,
} from "ui";
import { useCustomSlots } from "../../../../apis/tryst/get-custom-slots";
import useGetQueueConfig from "../../../../apis/tryst/get-queue-config";
import useQueueStore from "../../../../stores/queue-store";
import { useMyAccessProfileV2 } from "../../../../apis/tryst/get-my-access-profile";

function AvailableSlotsList({
  onSlotSelect,
}: {
  onSlotSelect: (props: {
    slot: SlotTime;
    docId: string;
    clinicId: string;
  }) => void;
}) {
  const [formState, setFormState] = useState<{
    clinicId?: string;
    slotDuration: number;
    slotTime?: SlotTime;
    customSelectedDaySlot: CUSTOM_SLOT_TYPE;
    customSlotDate: Date;
    docId?: string;
  }>({
    customSelectedDaySlot: CUSTOM_SLOT_TYPE.TODAY,
    customSlotDate: new Date(),
    slotDuration: 15,
  });

  const { data: myAccessProfile } = useMyAccessProfileV2();
  const { clinicId } = useQueueStore();

  const { data: queueConfResponse, isLoading } = useGetQueueConfig();
  const { data: queueConfData } = queueConfResponse || {};

  const {} = queueConfData || {};

  const { customSelectedDaySlot, customSlotDate, slotDuration, slotTime } =
    formState;

  useEffect(() => {
    if (!isLoading) {
      setFormState((prev) => ({
        ...prev,
        clinicId: clinicId,
      }));
    }
  }, [isLoading, clinicId]);

  const selectedClinicName =
    myAccessProfile?.clinics.find((clinic) => clinic.id === formState.clinicId)
      ?.name || "";

  const { data: slotsData, isLoading: isSlotsFetching } = useCustomSlots({
    clinicIdToFetch: formState.clinicId,
  });

  return (
    <PopoverContainerUi className="dr-min-h-502 dr-overflow-y-auto dr-h-502 dr-max-w-408 dr-w-408 dr-bg-darwin-neutral-0 BodyBold dr-rounded-16 dr-flex dr-flex-col dr-justify-between dr-relative">
      <div>
        {(myAccessProfile?.clinics || []).length > 1 ? (
          <>
            <div className="dr-px-16 dr-py-16 dr-flex dr-flex-col dr-space-y-16">
              <p>Select Clinic</p>
              <InputField
                suggestions={myAccessProfile?.clinics}
                value={selectedClinicName}
                onSuggestionSelect={({ id }): void => {
                  setFormState((prev) => ({ ...prev, clinicId: id }));
                }}
                customSuffix={
                  <div className="dr-h-full dr-flex dr-items-center dr-justify-center dr-px-12 dr-rounded-r-8">
                    <ChevronDownRegularIcon className="dr-w-14 dr-h-14 dr-text-darwin-touch-neutral-400" />
                  </div>
                }
                customPrefix={
                  <div className="dr-h-full dr-flex dr-items-center dr-justify-center dr-pl-12 dr-rounded-r-8">
                    <HouseMedicalChimneyRegularIcon className="dr-w-16 dr-h-16" />
                  </div>
                }
              />
            </div>
            <div className="dr-w-full dr-h-1 dr-bg-doctor-darwin-neutral-100" />
          </>
        ) : null}

        <SelectDateComponentUi
          customSelectedDaySlot={customSelectedDaySlot}
          customSlotDate={customSlotDate}
          onDateChange={(e) => {
            // track("select_date", {
            //   date: e.target.value,
            // });
            const selectedDateType = e.target.value;

            let exactDateForSlot: Date | undefined = undefined;

            switch (e.target.value) {
              case "today":
                exactDateForSlot = new Date();
                break;
              case "tomorrow":
                exactDateForSlot = new Date(
                  dayjs(Date.now()).add(1, "day").format("YYYY-MM-DD")
                );
                break;
              case "saturday":
                exactDateForSlot = new Date(
                  dayjs(Date.now()).day(6).format("YYYY-MM-DD")
                );
                break;
              case "custom":
                break;
            }

            setFormState((prev) => ({
              ...prev,
              customSelectedDaySlot: selectedDateType,
              customSlotDate: exactDateForSlot
                ? exactDateForSlot
                : prev.customSlotDate,
              slotTime: undefined,
            }));
          }}
          setCustomSlotDate={(date) => {
            setFormState((prev) => ({ ...prev, customSlotDate: date }));
          }}
        />
        <div className="dr-w-full dr-h-1 dr-bg-darwin-neutral-100" />
        <SelectDurationComponentUi
          customSlotDuration={slotDuration}
          onDurationChange={(duration) =>
            setFormState((prev) => ({ ...prev, slotDuration: duration }))
          }
        />
        <div className="dr-w-full dr-h-1 dr-bg-darwin-neutral-100" />
        <SelectAvailableSlotComponentUi
          selectedSlot={slotTime}
          isSlotsFetching={isSlotsFetching}
          filteredSlots={
            slotsData?.slotsByDate?.[dateFormat(customSlotDate, "yyyy-mm-dd")] || []
          }
          onSlotSelect={(slot) =>
            setFormState((prev) => ({ ...prev, slotTime: slot }))
          }
        />
      </div>
      <div className="dr-w-full dr-p-16 dr-rounded-t-16 dr-sticky dr-bottom-0 dr-bg-darwin-neutral-0 dr-drop-shadow-6">
        <PrimaryButton
          title="Select Slot"
          onClick={() => {
            if (formState.slotTime) {
              onSlotSelect({
                slot: formState.slotTime,
                docId: formState.docId!,
                clinicId: formState.clinicId!,
              });
            }
          }}
          extraClass="dr-w-full"
          isDisabled={slotTime === undefined}
        />
      </div>
    </PopoverContainerUi>
  );
}

export default AvailableSlotsList;
